/**
 * Append a cache-busting query param so browsers/CDNs refetch when the
 * underlying file path is unchanged (e.g. overwritten logo.png).
 */
export function cacheBustUrl(
  url: string | undefined | null,
  version: string | number = Date.now(),
): string {
  if (!url) {
    return '';
  }

  if (url.startsWith('data:')) {
    return url;
  }

  try {
    const parsed = new URL(
      url,
      typeof globalThis !== 'undefined' && globalThis.window?.location?.origin
        ? globalThis.window.location.origin
        : 'https://localhost',
    );
    parsed.searchParams.set('v', String(version));
    return parsed.toString();
  } catch {
    const [base] = url.split('?');
    return `${base}?v=${version}`;
  }
}

interface CollegeMediaFields {
  collegeLogo?: string;
  collegeBanner?: string;
  collegePhoto?: string;
}

/** Cache-bust college media fields on a single college record. */
export function cacheBustCollegeMedia<T extends CollegeMediaFields>(
  college: T,
  version: string | number = Date.now(),
): T {
  if (!college) {
    return college;
  }

  const next = { ...college };

  if (next.collegeLogo) {
    next.collegeLogo = cacheBustUrl(next.collegeLogo, version);
  }
  if (next.collegeBanner) {
    next.collegeBanner = cacheBustUrl(next.collegeBanner, version);
  }
  if (next.collegePhoto) {
    next.collegePhoto = cacheBustUrl(next.collegePhoto, version);
  }

  return next;
}

/** Cache-bust college media fields on each item in a search/list result. */
export function cacheBustCollegeMediaList<T extends CollegeMediaFields>(
  colleges: T[] | undefined | null,
  version: string | number = Date.now(),
): T[] {
  if (!colleges?.length) {
    return colleges ?? [];
  }

  return colleges.map((college) => cacheBustCollegeMedia(college, version));
}

export function cacheBustProfileMedia<T extends Record<string, unknown>>(
  profile: T,
  version: string | number = Date.now(),
): T {
  if (!profile || !('profileImage' in profile)) {
    return profile;
  }

  const next = { ...profile } as T & { profileImage?: string };
  next.profileImage = cacheBustUrl(next.profileImage, version);
  return next as T;
}
